import React, { FC, useEffect, useState } from "react";

import "../styles.scss";
import "../scripts";

import {
  KameleonElement,
  primaryCollectionBlock,
  getDefaultProps,
} from "@kameleon-core/lib";
import { Box } from "theme-ui";
import { WebflowComponentProps, webflowHOC } from "@kameleon-common/webflow-js";
// eslint-disable-next-line import/no-extraneous-dependencies
import { Variants } from "@kameleon-util/global-theme";
import {
  blockConfig,
  ContentCollectionItem,
  ContentCollectionItemType,
} from "./components/content-collection-item";

import blockinfo from "./blockinfo.json";
import defaults from "./defaults.json";
import {
  ContentCollectionType as ComponentType,
  layoutOptions,
} from "./types/prop-types";
import getEvents from "./js/webflow";

type ContentCollectionType = {
  PrimaryBlocks: ContentCollectionItemType[];
} & ComponentType;

type PassThroughProps = {
  accordionIds: { accordionId: string; columnId: string }[];
};
type CollectionWebflowType = WebflowComponentProps<
  ContentCollectionType,
  PassThroughProps
>;

const getAccordionIds = (primaryBlocks: any[]) =>
  primaryBlocks.map((_, index) => ({
    accordionId: `a${index}`,
    columnId: `c${index}`,
  }));

const Component: FC<CollectionWebflowType> = ({
  PrimaryBlocks,
  tagline,
  title,
  description,
  passThrough,
}: CollectionWebflowType) => {
  const [accordionIds, setAccordionIds] = useState<
    PassThroughProps["accordionIds"]
  >(getAccordionIds(PrimaryBlocks));

  const updateAccordionIds = () => {
    const updatedAccordionIds = getAccordionIds(PrimaryBlocks);
    setAccordionIds(updatedAccordionIds);
    passThrough?.({ accordionIds: updatedAccordionIds });
  };

  useEffect(() => {
    updateAccordionIds();
  }, [PrimaryBlocks]);

  useEffect(() => {
    updateAccordionIds();
  }, []);

  return (
    <Box className="primary-collection-content-collection-accordion-horizontal-v1 section_layout351">
      <Box className="padding-global container-large padding-section-large">
        <Box className="margin-bottom margin-xxlarge max-width-large">
          {tagline && (
            <Box className="margin-bottom margin-xsmall">
              <Variants.TagLine>{tagline}</Variants.TagLine>
            </Box>
          )}
          <Box className="margin-bottom margin-small">
            <Variants.Heading>{title}</Variants.Heading>
          </Box>
          <Variants.ParagraphMedium>{description}</Variants.ParagraphMedium>
        </Box>
        <Variants.AccordionHorizontal className="layout351_component">
          {PrimaryBlocks?.slice(0, 5).map((primaryBlockProps, i) => {
            const itemProps = {
              ...primaryBlockProps,
              _isActive: i === 0,
              _accordionId: accordionIds[i]?.accordionId,
              _columnId: accordionIds[i]?.columnId,
              _index: i,
            };
            return (
              <Variants.AccordionHorizontal
                key={i}
                data-w-id={accordionIds[i]?.accordionId}
                className={`layout351_accordion ${i === 0 ? "active" : ""}`}
              >
                <ContentCollectionItem {...itemProps} />
              </Variants.AccordionHorizontal>
            );
          })}
        </Variants.AccordionHorizontal>
      </Box>
    </Box>
  );
};

const ComponentWithWebflow = webflowHOC<
  CollectionWebflowType,
  PassThroughProps
>(Component, [
  {
    module: "ix2",
    getEvents: (pageId, passThrough): object =>
      getEvents(pageId, passThrough?.accordionIds ?? []),
  },
]);

export const ContentCollection = primaryCollectionBlock<ContentCollectionType>(
  blockConfig,
  ({ PrimaryBlocks, ...props }: ContentCollectionType) => (
    <ComponentWithWebflow PrimaryBlocks={PrimaryBlocks} {...props} />
  )
);

ContentCollection.defaultProps =
  getDefaultProps<ContentCollectionType>(defaults);

export const Element: KameleonElement = {
  component: ContentCollection,
  blockInfo: blockinfo,
  defaultProps: {
    layoutProps: defaults.layoutProps,
    contentProps: defaults.contentProps,
  },
  layoutOptions,
};
